import { ChangeEvent } from 'react';
import { CheckmarkCheckedIcon } from '../../assets/svg';
import {
  CheckmarkCircle,
  CheckmarkNativeInput,
  CheckmarkRoot,
} from './CheckmarkInput.styled';

type Props = {
  checked: boolean;
  /** Called with the new value. */
  onChange: (checked: boolean) => void;
  /** Faded to 45%, not clickable, "not allowed" cursor. */
  disabled?: boolean;
  /** Diameter in px. */
  size?: number;
  /** Accessible name, when no label around it says what it ticks. */
  ariaLabel?: string;
  id?: string;
  name?: string;
};

/**
 * A checkbox drawn as a round checkmark, the way Connect Brands marks a picked
 * menu item: the same red circle-check icon when checked, an empty grey circle
 * when not.
 *
 * It is a real `<input type="checkbox">` laid over the circle, not an icon:
 * Space toggles it, it shows a focus ring for the keyboard, screen readers
 * announce it as a checkbox, and inside a `<label>` a click anywhere on the
 * label toggles it.
 */
export const CheckmarkInput = ({
  checked,
  onChange,
  disabled = false,
  size = 24,
  ariaLabel,
  id,
  name,
}: Props) => (
  <CheckmarkRoot $size={size}>
    <CheckmarkNativeInput
      type="checkbox"
      id={id}
      name={name}
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange(event.target.checked)
      }
    />
    <CheckmarkCircle
      aria-hidden
      $checked={checked}
      $disabled={disabled}
      $size={size}
    >
      {checked && <CheckmarkCheckedIcon />}
    </CheckmarkCircle>
  </CheckmarkRoot>
);
